import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { 
  EyeIcon, 
  DocumentTextIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  PauseCircleIcon,
  CalendarIcon,
  CreditCardIcon,
  ArrowDownTrayIcon,
  BanknotesIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { policyAPI, requestAPI, healthReimbursementAPI, healthServiceAPI, uploadAPI, formatCurrency, formatDateTime, getOfferTypeLabel } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import SavingsDetailsModal, { generateSavingsStatementPdfForData } from '../components/SavingsDetailsModal';
import HealthPolicyDetailsModal, { generateHealthContributionsStatementPdfForPolicy } from '../components/HealthPolicyDetailsModal';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const MyPolicies = () => {
  const queryClient = useQueryClient();
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showHealthDetailsModal, setShowHealthDetailsModal] = useState(false);
  const [showSavingsModal, setShowSavingsModal] = useState(false);
  const [showTerminationModal, setShowTerminationModal] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [terminationReason, setTerminationReason] = useState('');
  const [terminationNotes, setTerminationNotes] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 12;
  const [showHealthReimbursementModal, setShowHealthReimbursementModal] = useState(false);
  const [reimbursementForm, setReimbursementForm] = useState({
    reimbursementType: '', typeId: '', amountSurcout: '', amountTicketModerateur: '',
    amountTotalRequested: '', reasonText: '', beneficiaryId: '', serviceTypeId: '',
    executedAt: '', etablissement: '', praticien: '', costTotal: '',
    costConventionne: '', assurancePrincipalePart: '', ticketModerateur: '', surcout: '', attachmentFile: []
  });
  const [reimbursementNotes, setReimbursementNotes] = useState('');

  // Récupérer mes polices
  const { data: policiesData, isLoading } = useQuery(
    'my-policies',
    () => policyAPI.getUserPolicies(),
    {
      refetchInterval: 60000, // Rafraîchir toutes les minutes
    }
  );

  const policies = policiesData?.data?.policies || [];

  // Récupérer les types de demande
  const { data: requestTypesRaw } = useQuery(
    'request-types',
    async () => {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/request-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Le backend peut renvoyer { success: true, data: [...] } ou directement un tableau
      return response.data;
    }
  );

  const requestTypes = Array.isArray(requestTypesRaw?.data)
    ? requestTypesRaw.data
    : Array.isArray(requestTypesRaw)
      ? requestTypesRaw
      : [];

  const terminationType = requestTypes.find(t =>
    typeof t.libelle === 'string' && t.libelle.toLowerCase() === 'résiliation'
  );

  const healthRequestTypes = requestTypes.filter(t =>
    typeof t.libelle === 'string' && t.libelle.startsWith('Remboursement')
  );

  // Types de prestations de santé
  const { data: healthServiceTypesData } = useQuery(
    'health-service-types',
    () => healthServiceAPI.getHealthServiceTypes()
  );
  const healthServiceTypes = healthServiceTypesData?.data?.data || healthServiceTypesData?.data || [];

  // Mutation pour créer une demande de remboursement santé
  const createHealthReimbursementMutation = useMutation(
    (data) => healthReimbursementAPI.createHealthReimbursement(data),
    {
      onSuccess: () => {
        toast.success('Demande de remboursement santé créée avec succès');
        queryClient.invalidateQueries('my-policies');
        setShowHealthReimbursementModal(false);
        setReimbursementForm({
          reimbursementType: '', typeId: '', amountSurcout: '', amountTicketModerateur: '',
          amountTotalRequested: '', reasonText: '', beneficiaryId: '', serviceTypeId: '',
          executedAt: '', etablissement: '', praticien: '', costTotal: '',
          costConventionne: '', assurancePrincipalePart: '', ticketModerateur: '', surcout: '', attachmentFile: []
        });
        setReimbursementNotes('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erreur lors de la création de la demande de remboursement santé');
      }
    }
  );

  // Mutation pour créer une demande de résiliation
  const createRequestMutation = useMutation(
    (data) => requestAPI.createRequest(data),
    {
      onSuccess: () => {
        toast.success('Demande de résiliation envoyée avec succès');
        queryClient.invalidateQueries('my-policies');
        setShowTerminationModal(false);
        setSelectedPolicy(null);
        setTerminationReason('');
        setTerminationNotes('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erreur lors de l\'envoi de la demande');
      }
    }
  );

  const handleViewDetails = async (policy) => {
    try {
      const response = await policyAPI.getPolicyDetails(policy.id);
      setSelectedPolicy(response.data);
      setCurrentPage(1); // Réinitialiser la pagination
      
      // Si c'est une police d'épargne, afficher le modal spécifique
      if (response.data.subscription?.offer?.type === 'SAVINGS') {
        setShowSavingsModal(true);
      } else if (response.data.subscription?.offer?.type === 'HEALTH_COVERAGE') {
        // Pour les produits santé, utiliser une fenêtre de détail enrichie alignée sur l'admin
        setShowHealthDetailsModal(true);
      } else {
        setShowDetailsModal(true);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des détails');
    }
  };

  // Téléchargement d'un relevé selon le type de produit
  const handleDownloadStatement = async (policy) => {
    try {
      // Pour les produits d'épargne, on génère directement le PDF en réutilisant la logique du modal
      if (policy.subscription?.offer?.type === 'SAVINGS') {
        const response = await policyAPI.getPolicySavingsDetails(policy.id);
        const savings = response.data;

        if (!savings) {
          toast.error('Impossible de récupérer les données d’épargne');
          return;
        }

        // Reproduire le regroupement par années utilisé dans SavingsDetailsModal
        const operations = Array.isArray(savings.operations) ? savings.operations : [];
        const operationsByYear = operations.reduce((acc, op) => {
          if (!acc[op.year]) {
            acc[op.year] = [];
          }
          acc[op.year].push(op);
          return acc;
        }, {});

        Object.keys(operationsByYear).forEach((year) => {
          operationsByYear[year] = operationsByYear[year]
            .slice()
            .sort((a, b) => {
              const getOperationSortDate = (op) => {
                if (!op) return new Date(0);
                if (op.type === 'RACHAT' && op.dateRachat) {
                  return new Date(op.dateRachat);
                }
                if (op.type === 'INTERET' && op.periodeFin) {
                  return new Date(op.periodeFin);
                }
                if (op.year) {
                  return new Date(Number(op.year), 11, 31);
                }
                return new Date(0);
              };
              return getOperationSortDate(b) - getOperationSortDate(a);
            });
        });

        const years = Object.keys(operationsByYear).sort((a, b) => b - a);

        await generateSavingsStatementPdfForData(savings, years, operationsByYear);
      } else if (policy.subscription?.offer?.type === 'HEALTH_COVERAGE') {
        // Pour les produits santé, générer un relevé de cotisations aligné sur le relevé d'épargne,
        // en utilisant les détails complets de la police
        const detailsResponse = await policyAPI.getPolicyDetails(policy.id);
        const detailedPolicy = detailsResponse.data;
        await generateHealthContributionsStatementPdfForPolicy(detailedPolicy);
      } else {
        toast.success('Téléchargement du relevé - Fonctionnalité à venir');
      }
    } catch (error) {
      console.error('Erreur lors de la préparation du relevé d’épargne:', error);
      toast.error('Erreur lors de la préparation du relevé');
    }
  };

  const handleRequestTermination = (policy) => {
    setSelectedPolicy(policy);
    setSelectedTypeId(terminationType?.id || '');
    setTerminationReason('');
    setTerminationNotes('');
    setShowTerminationModal(true);
  };

  const handleCreateHealthReimbursement = async () => {
    if (!selectedPolicy) { toast.error('Aucune police sélectionnée'); return; }
    const { beneficiaryId, serviceTypeId, executedAt, etablissement, praticien,
      costTotal, costConventionne, assurancePrincipalePart, ticketModerateur, surcout,
      typeId, amountTicketModerateur, amountSurcout, attachmentFile } = reimbursementForm;
    if (!beneficiaryId || !serviceTypeId || !executedAt || !etablissement || !praticien ||
        !costTotal || !costConventionne || !assurancePrincipalePart || !ticketModerateur ||
        !typeId || !attachmentFile || attachmentFile.length === 0) {
      toast.error('Veuillez renseigner tous les champs obligatoires');
      return;
    }
    try {
      const healthServiceResponse = await healthServiceAPI.createHealthService({
        policyId: selectedPolicy.id, mutualisteId: beneficiaryId, typeId: serviceTypeId,
        executedAt, etablissement, praticien,
        costTotal: parseFloat(costTotal) || 0,
        costConventionne: parseFloat(costConventionne) || 0,
        assurancePrincipalePart: parseFloat(assurancePrincipalePart) || 0,
        ticketModerateur: parseFloat(ticketModerateur) || 0,
        surcout: parseFloat(surcout) || 0
      });
      const healthService = healthServiceResponse.data?.data || healthServiceResponse.data;
      const selectedRequestType = requestTypes.find((t) => t.id === typeId);
      const reimbursementType = selectedRequestType?.libelle || 'Remboursement';
      const ticketValue = parseFloat(amountTicketModerateur || ticketModerateur) || 0;
      const surcoutValue = parseFloat(amountSurcout || surcout) || 0;
      let amountTotalRequested = 0;
      if (reimbursementType === 'Remboursement de surcoût') {
        amountTotalRequested = surcoutValue * 0.75;
      } else if (reimbursementType === 'Remboursement de ticket modérateur') {
        amountTotalRequested = ticketValue;
      } else if (reimbursementType === 'Remboursement de ticket modérateur et de surcoût') {
        amountTotalRequested = (surcoutValue * 0.75) + ticketValue;
      } else {
        amountTotalRequested = surcoutValue + ticketValue;
      }
      let attachments = [];
      if (Array.isArray(attachmentFile) && attachmentFile.length > 0) {
        const formData = new FormData();
        attachmentFile.forEach((file) => { formData.append('files', file); });
        const uploadResponse = await uploadAPI.uploadHealthReimbursementFiles(formData);
        attachments = uploadResponse.data?.data || uploadResponse.data || [];
      }
      createHealthReimbursementMutation.mutate({
        policyId: selectedPolicy.id, typeId,
        healthServiceId: healthService.id,
        amountTicketModerateur: ticketValue, amountSurcout: surcoutValue, amountTotalRequested,
        reasonText: reimbursementNotes.trim() || 'Prestations FPM',
        reimbursementType, attachments,
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la création de la prestation de santé');
    }
  };

  const handleOpenHealthReimbursement = () => {
    if (!selectedPolicy) return;
    const adherent = selectedPolicy?.subscription?.mutualiste;
    setReimbursementForm({
      reimbursementType: '', typeId: '', amountSurcout: '', amountTicketModerateur: '',
      amountTotalRequested: '', reasonText: '',
      beneficiaryId: adherent?.id || '',
      serviceTypeId: '', executedAt: '', etablissement: '', praticien: '',
      costTotal: '', costConventionne: '', assurancePrincipalePart: '',
      ticketModerateur: '', surcout: '', attachmentFile: []
    });
    setReimbursementNotes('');
    setShowHealthReimbursementModal(true);
  };

  const handleDownloadHealthHealthStatement = async () => {
    if (!selectedPolicy) return;
    await generateHealthContributionsStatementPdfForPolicy(selectedPolicy);
  };

  const confirmTermination = () => {
    if (!selectedTypeId) {
      toast.error('Veuillez sélectionner un type de demande');
      return;
    }

    if (!terminationReason.trim()) {
      toast.error('Veuillez indiquer la raison de votre demande');
      return;
    }

    createRequestMutation.mutate({
      policyId: selectedPolicy.id,
      typeId: selectedTypeId,
      reasonText: terminationReason,
      notes: terminationNotes
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ACTIVE':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'PENDING':
        return <PauseCircleIcon className="h-5 w-5 text-yellow-500" />;
      case 'SUSPENDED':
        return <XCircleIcon className="h-5 w-5 text-orange-500" />;
      case 'TERMINATED':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'PENDING': return 'warning';
      case 'SUSPENDED': return 'orange';
      case 'TERMINATED': return 'danger';
      default: return 'gray';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'ACTIVE': return 'Active';
      case 'PENDING': return 'En attente';
      case 'SUSPENDED': return 'Suspendue';
      case 'TERMINATED': return 'Résiliée';
      default: return status;
    }
  };

  const getPaymentStatus = (policy) => {
    const totalContributions = policy.expectedContributions || policy.contributions?.length || 0;
    if (totalContributions === 0) return 'À jour';
    const percentage = (policy.receivedContributions / totalContributions) * 100;
    if (percentage >= 100) return 'À jour';
    if (percentage >= 80) return 'Presque à jour';
    if (percentage >= 50) return 'En retard';
    return 'Très en retard';
  };

  const getPaymentStatusColor = (policy) => {
    const totalContributions = policy.expectedContributions || policy.contributions?.length || 0;
    if (totalContributions === 0) return 'success';
    const percentage = (policy.receivedContributions / totalContributions) * 100;
    if (percentage >= 100) return 'success';
    if (percentage >= 80) return 'warning';
    if (percentage >= 50) return 'orange';
    return 'danger';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <h1 className="text-2xl font-bold text-primary-900 mb-2">
          Mes polices d'assurance
        </h1>
        <p className="text-gray-600">
          Consultez vos polices d'assurance et suivez vos cotisations
        </p>
      </div>

      {/* Résumé */}
      {policies.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total polices</p>
                <p className="text-2xl font-semibold text-gray-900">{policies.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Polices actives</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {policies.filter(p => p.status === 'ACTIVE').length}
                </p>
              </div>
            </div>
          </div>

          {/* Carte Cotisations totales masquée */}
        </div>
      )}

      {/* Modal de détails santé partagé avec l'admin */}
      {showHealthDetailsModal && selectedPolicy && (
        <HealthPolicyDetailsModal
          policy={selectedPolicy}
          onClose={() => {
            setShowHealthDetailsModal(false);
            setSelectedPolicy(null);
          }}
          onOpenHealthReimbursement={handleOpenHealthReimbursement}
          onDownloadHealthStatement={handleDownloadHealthHealthStatement}
          onRequestTermination={(policy) => {
            setShowHealthDetailsModal(false);
            handleRequestTermination(policy);
          }}
        />
      )}

      {/* Liste des polices */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        {policies.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune police</h3>
            <p className="mt-1 text-sm text-gray-500">
              Vous n'avez pas encore de police d'assurance active.
            </p>
            <div className="mt-6">
              <button
                onClick={() => window.location.href = '/offers'}
                className="btn-primary"
              >
                Découvrir nos produits
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            {policies.map((policy) => (
              <div key={policy.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                {/* En-tête de la carte */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                      {getStatusIcon(policy.status)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {policy.subscription?.offer?.name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Police n° {policy.policyNumber}
                      </p>
                    </div>
                  </div>
                  <span className={`badge badge-${getStatusColor(policy.status?.libelle || policy.status)}`}>
                    {getStatusLabel(policy.status?.libelle || policy.status)}
                  </span>
                </div>

                {/* Informations principales */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-500">Type de produit</p>
                    <p className="text-sm font-medium text-gray-900">
                      {getOfferTypeLabel(policy.subscription?.offer?.type)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Montant mensuel</p>
                    <p className="text-sm font-medium text-gray-900">
                      {formatCurrency(policy.monthlyAmount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Bénéficiaires</p>
                    <p className="text-sm font-medium text-gray-900">
                      {policy.beneficiariesCount} personne{policy.beneficiariesCount > 1 ? 's' : ''}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Date d'effet</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(policy.effectiveDate).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>

                {/* Statut des paiements */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-gray-500">Cotisations</p>
                    <span className={`badge badge-${getPaymentStatusColor(policy)}`}>
                      {getPaymentStatus(policy)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          getPaymentStatusColor(policy) === 'success' ? 'bg-green-500' :
                          getPaymentStatusColor(policy) === 'warning' ? 'bg-yellow-500' :
                          getPaymentStatusColor(policy) === 'orange' ? 'bg-orange-500' : 'bg-red-500'
                        }`}
                        style={{ 
                          width: `${(policy.expectedContributions || policy.contributions?.length || 0) > 0 ? 
                            Math.min(100, (policy.receivedContributions / (policy.expectedContributions || policy.contributions?.length || 0)) * 100) : 100}%` 
                        }}
                      />
                    </div>
                    <span className="text-xs text-gray-500">
                      {policy.receivedContributions}/{policy.expectedContributions || policy.contributions?.length || 0}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Total reçu: {formatCurrency(policy.totalAmountReceived)}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleViewDetails(policy)}
                    className="flex-1 btn-primary btn-sm flex items-center justify-center"
                  >
                    <EyeIcon className="h-4 w-4 mr-2" />
                    Détails
                  </button>
                  <button
                    onClick={() => handleDownloadStatement(policy)}
                    className="btn-secondary btn-sm flex items-center justify-center"
                    title="Télécharger un relevé"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de détails */}
      {showDetailsModal && selectedPolicy && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDetailsModal(false)} />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Police {selectedPolicy.policyNumber}
                  </h3>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Informations police */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">Informations de la police</h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        {getStatusIcon(selectedPolicy.status)}
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {selectedPolicy.subscription?.offer?.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {getOfferTypeLabel(selectedPolicy.subscription?.offer?.type)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Statut</p>
                          <span className={`badge badge-${getStatusColor(selectedPolicy.status?.libelle || selectedPolicy.status)}`}>
                            {getStatusLabel(selectedPolicy.status?.libelle || selectedPolicy.status)}
                          </span>
                        </div>
                        <div>
                          <p className="text-gray-500">Date d'effet</p>
                          <p className="font-medium">{new Date(selectedPolicy.effectiveDate).toLocaleDateString('fr-FR')}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Bénéficiaires</p>
                          <p className="font-medium">{selectedPolicy.beneficiariesCount}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Montant mensuel</p>
                          <p className="font-medium">{formatCurrency(selectedPolicy.monthlyAmount)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Statistiques des paiements */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">Suivi des cotisations</h4>
                    <div className="space-y-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-600">Progression</span>
                          <span className={`badge badge-${getPaymentStatusColor(selectedPolicy)}`}>
                            {getPaymentStatus(selectedPolicy)}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                          <div 
                            className={`h-3 rounded-full ${
                              getPaymentStatusColor(selectedPolicy) === 'success' ? 'bg-green-500' :
                              getPaymentStatusColor(selectedPolicy) === 'warning' ? 'bg-yellow-500' :
                              getPaymentStatusColor(selectedPolicy) === 'orange' ? 'bg-orange-500' : 'bg-red-500'
                            }`}
                            style={{ 
                              width: `${(selectedPolicy.contributions?.length || selectedPolicy.expectedContributions) > 0 ? 
                                Math.min(100, (selectedPolicy.receivedContributions / (selectedPolicy.contributions?.length || selectedPolicy.expectedContributions)) * 100) : 100}%` 
                            }}
                          />
                        </div>
                        <p className="text-sm text-gray-600">
                          {selectedPolicy.receivedContributions} sur {selectedPolicy.contributions?.length || selectedPolicy.expectedContributions} cotisations reçues
                        </p>
                      </div>

                      <div className="grid grid-cols-1 gap-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Total reçu</span>
                          <span className="text-sm font-medium">{formatCurrency(selectedPolicy.totalAmountReceived)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Montant attendu</span>
                          <span className="text-sm font-medium">
                            {formatCurrency((selectedPolicy.contributions?.length || selectedPolicy.expectedContributions) * selectedPolicy.monthlyAmount)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Historique des cotisations */}
                {selectedPolicy.contributions && selectedPolicy.contributions.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 mb-3">
                      Historique des cotisations ({selectedPolicy.contributions.length})
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Échéance</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date paiement</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mode de paiement</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {selectedPolicy.contributions
                            .sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate))
                            .slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
                            .map((contribution) => (
                            <tr key={contribution.id} className="hover:bg-gray-50">
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {new Date(contribution.dueDate).toLocaleDateString('fr-FR')}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {formatCurrency(contribution.amount)}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                <span className={`badge badge-${
                                  contribution.status === 'PAID' ? 'success' : 
                                  contribution.status === 'OVERDUE' ? 'danger' : 'warning'
                                }`}>
                                  {contribution.status === 'PAID' ? 'Payée' : 
                                   contribution.status === 'OVERDUE' ? 'En retard' : 'En attente'}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-500">
                                {contribution.paidDate ? new Date(contribution.paidDate).toLocaleDateString('fr-FR') : '-'}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-500">
                                {contribution.paymentMethod || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination */}
                    {selectedPolicy.contributions.length > ITEMS_PER_PAGE && (
                      <div className="mt-4 flex items-center justify-between">
                        <div className="text-sm text-gray-700">
                          Page {currentPage} sur {Math.ceil(selectedPolicy.contributions.length / ITEMS_PER_PAGE)}
                          {' '}({selectedPolicy.contributions.length} cotisations au total)
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                          >
                            <ChevronLeftIcon className="h-4 w-4 mr-1" />
                            Précédent
                          </button>
                          <button
                            onClick={() => setCurrentPage(prev => Math.min(Math.ceil(selectedPolicy.contributions.length / ITEMS_PER_PAGE), prev + 1))}
                            disabled={currentPage === Math.ceil(selectedPolicy.contributions.length / ITEMS_PER_PAGE)}
                            className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                          >
                            Suivant
                            <ChevronRightIcon className="h-4 w-4 ml-1" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="mt-6">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => toast.success('Téléchargement PDF - Fonctionnalité à venir')}
                      className="flex-1 btn-primary btn-sm flex items-center justify-center"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                      Télécharger l'attestation
                    </button>
                    <button
                      onClick={() => toast.success('Paiement en ligne - Fonctionnalité à venir')}
                      className="flex-1 btn-secondary btn-sm flex items-center justify-center"
                    >
                      <CreditCardIcon className="h-4 w-4 mr-2" />
                      Payer une cotisation
                    </button>
                    {selectedPolicy.status === 'ACTIVE' && (
                      <button
                        onClick={() => {
                          setShowDetailsModal(false);
                          handleRequestTermination(selectedPolicy);
                        }}
                        className="flex-1 btn-sm bg-red-600 text-white hover:bg-red-700 flex items-center justify-center"
                      >
                        <XCircleIcon className="h-4 w-4 mr-2" />
                        Demander la résiliation
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal détails épargne */}
      {showSavingsModal && selectedPolicy && (
        <SavingsDetailsModal
          policy={selectedPolicy}
          onClose={() => {
            setShowSavingsModal(false);
            setSelectedPolicy(null);
          }}
        />
      )}

      {/* Modal de demande de résiliation */}
      {showTerminationModal && selectedPolicy && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowTerminationModal(false)} />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <XCircleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Demande de résiliation
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 mb-4">
                        Police n° <strong>{selectedPolicy.policyNumber}</strong>
                      </p>
                      <p className="text-sm text-gray-500 mb-4">
                        Vous êtes sur le point de faire une demande de résiliation pour cette police. 
                        Cette demande sera examinée par nos services.
                      </p>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Raison de la résiliation <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            value={terminationReason}
                            onChange={(e) => setTerminationReason(e.target.value)}
                            rows={3}
                            className="form-input"
                            placeholder="Expliquez la raison de votre demande..."
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Notes additionnelles (optionnel)
                          </label>
                          <textarea
                            value={terminationNotes}
                            onChange={(e) => setTerminationNotes(e.target.value)}
                            rows={2}
                            className="form-input"
                            placeholder="Informations complémentaires..."
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmTermination}
                  disabled={createRequestMutation.isLoading || !terminationReason.trim()}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createRequestMutation.isLoading ? (
                    <LoadingSpinner size="small" className="mr-2" />
                  ) : null}
                  Envoyer la demande
                </button>
                <button
                  type="button"
                  onClick={() => setShowTerminationModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Modal de demande de remboursement santé */}
      {showHealthReimbursementModal && selectedPolicy && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowHealthReimbursementModal(false)} />
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                    <BanknotesIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full relative">
                    <button type="button" onClick={() => setShowHealthReimbursementModal(false)}
                      className="absolute top-0 right-0 text-gray-400 hover:text-gray-600 focus:outline-none">
                      <span className="sr-only">Fermer</span>×
                    </button>
                    <h3 className="text-lg leading-6 font-medium text-gray-900 pr-6">Demande de remboursement</h3>
                    <div className="mt-2">
                      <div className="space-y-4">
                        <div className="border-b border-gray-200 pb-4 mb-4">
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Bénéficiaire <span className="text-red-500">*</span></label>
                                <select className="form-input h-11" value={reimbursementForm.beneficiaryId}
                                  onChange={(e) => setReimbursementForm(prev => ({ ...prev, beneficiaryId: e.target.value }))}>
                                  <option value="">{String('Sélectionner un bénéficiaire')}</option>
                                  {selectedPolicy.subscription?.mutualiste && (
                                    <option value={selectedPolicy.subscription.mutualiste.id}>
                                      {String(`${selectedPolicy.subscription.mutualiste.nom} ${selectedPolicy.subscription.mutualiste.prenoms} (Adhérent)`)}
                                    </option>
                                  )}
                                  {Array.isArray(selectedPolicy.subscription?.mutualiste?.ayantsDroit) && selectedPolicy.subscription.mutualiste.ayantsDroit.map((ayant) => (
                                    <option key={ayant.id} value={ayant.id}>
                                      {String(`${ayant.nom} ${ayant.prenoms}${ayant.lienParente?.libelle ? ` (${ayant.lienParente.libelle})` : ''}`)}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Type de prestation <span className="text-red-500">*</span></label>
                                <select className="form-input h-11" value={reimbursementForm.serviceTypeId}
                                  onChange={(e) => setReimbursementForm(prev => ({ ...prev, serviceTypeId: e.target.value }))}>
                                  <option value="">{String('Sélectionner un type')}</option>
                                  {Array.isArray(healthServiceTypes) && healthServiceTypes.map((type) => (
                                    <option key={type.id} value={type.id}>{String(type.libelle)}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date de prestation <span className="text-red-500">*</span></label>
                                <input type="date" className="form-input h-11" value={reimbursementForm.executedAt}
                                  onChange={(e) => setReimbursementForm(prev => ({ ...prev, executedAt: e.target.value }))} />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Établissement <span className="text-red-500">*</span></label>
                                <input type="text" className="form-input" value={reimbursementForm.etablissement}
                                  onChange={(e) => setReimbursementForm(prev => ({ ...prev, etablissement: (e.target.value || '').toUpperCase() }))} />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Praticien <span className="text-red-500">*</span></label>
                                <input type="text" className="form-input" value={reimbursementForm.praticien}
                                  onChange={(e) => setReimbursementForm(prev => ({ ...prev, praticien: (e.target.value || '').toUpperCase() }))} />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Montant centre <span className="text-red-500">*</span></label>
                                <input type="number" className="form-input" value={reimbursementForm.costTotal} min="0"
                                  onChange={(e) => setReimbursementForm(prev => ({ ...prev, costTotal: e.target.value }))} />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Montant conv. <span className="text-red-500">*</span></label>
                                <input type="number" className="form-input" value={reimbursementForm.costConventionne} min="0"
                                  onChange={(e) => setReimbursementForm(prev => ({ ...prev, costConventionne: e.target.value }))} />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Part mutuelle <span className="text-red-500">*</span></label>
                                <input type="number" className="form-input" value={reimbursementForm.assurancePrincipalePart} min="0"
                                  onChange={(e) => setReimbursementForm(prev => ({ ...prev, assurancePrincipalePart: e.target.value }))} />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Ticket mod. <span className="text-red-500">*</span></label>
                                <input type="number" className="form-input" value={reimbursementForm.ticketModerateur} min="0"
                                  onChange={(e) => setReimbursementForm(prev => ({ ...prev, ticketModerateur: e.target.value, amountTicketModerateur: e.target.value }))} />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Surcoût</label>
                                <input type="number" className="form-input" value={reimbursementForm.surcout} min="0"
                                  onChange={(e) => setReimbursementForm(prev => ({ ...prev, surcout: e.target.value, amountSurcout: e.target.value }))} />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Type de remboursement <span className="text-red-500">*</span></label>
                              <select className="form-input h-11" value={reimbursementForm.typeId}
                                onChange={(e) => setReimbursementForm(prev => ({ ...prev, typeId: e.target.value }))}>
                                <option value="">{String('Sélectionner un type de remboursement')}</option>
                                {healthRequestTypes.map((type) => (
                                  <option key={type.id} value={type.id}>{String(type.libelle)}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Justificatifs <span className="text-red-500">*</span></label>
                              <input type="file" multiple className="form-input"
                                onChange={(e) => { const files = Array.from(e.target.files || []); setReimbursementForm(prev => ({ ...prev, attachmentFile: files })); }} />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Commentaires</label>
                            <textarea value={reimbursementNotes}
                              onChange={(e) => setReimbursementNotes((e.target.value || '').toUpperCase())}
                              rows={2} className="form-input" placeholder="Informations complémentaires..." />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button type="button" onClick={handleCreateHealthReimbursement}
                  disabled={createHealthReimbursementMutation.isLoading
                    || !reimbursementForm.beneficiaryId || !reimbursementForm.serviceTypeId
                    || !reimbursementForm.executedAt || !reimbursementForm.etablissement
                    || !reimbursementForm.praticien || !reimbursementForm.costTotal
                    || !reimbursementForm.costConventionne || !reimbursementForm.assurancePrincipalePart
                    || !reimbursementForm.ticketModerateur || !reimbursementForm.typeId
                    || !reimbursementForm.attachmentFile || reimbursementForm.attachmentFile.length === 0}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                  {createHealthReimbursementMutation.isLoading ? <LoadingSpinner size="small" className="mr-2" /> : null}
                  Soumettre la demande
                </button>
                <button type="button" onClick={() => setShowHealthReimbursementModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPolicies;
